import React, { useState } from 'react';
import {
  Badge,
  IconButton,
  Popover,
  Typography,
  Box,
  Button,
} from '@mui/material';
import { Notifications as NotificationsIcon, Close as CloseIcon } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Notification } from '../types';
import { notificationsService } from '../services/notificationsService';
import { navigateFromNotification } from '../utils/notificationNavigation';
import LoadingAnimation from './LoadingAnimation';
import { getZoomCompensatedSx } from '../utils/zoomCompensation';

interface NotificationsCenterProps {
  unreadCount: number;
  onUnreadCountChange: (count: number) => void;
}

const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b' };

const TYPE_LABELS: Record<string, string> = {
  new_time_off_request: 'Time off',
  time_off_approved: 'Approved',
  time_off_rejected: 'Declined',
  time_off_cancelled: 'Cancelled',
  project_assigned: 'Project',
  transmittal_sent: 'Transmittal',
  event_reminder: 'Calendar',
  timesheet_reminder: 'Timesheet',
  timesheet_updated: 'Timesheet',
  opportunity_next_action_due: 'Lead',
  opportunity_next_action_overdue: 'Overdue',
  document_upload_required: 'Upload',
  document_rejected: 'Rejected',
  document_checker_required: 'Check',
  document_approval_required: 'Approval',
  document_approver_required: 'Approval',
  document_preview_ready: 'Preview',
  document_preview_failed: 'Preview',
  document_approved: 'Approved',
  document_metadata_mismatch: 'Document',
  system: 'System',
};

function notificationTypeLabel(type: string): string {
  if (TYPE_LABELS[type]) return TYPE_LABELS[type];
  if (type.startsWith('holiday_balance_')) return 'Leave';
  if (type.startsWith('document_')) return 'Document';
  if (type.startsWith('time_off_')) return 'Time off';
  return type.replace(/_/g, ' ');
}

const NotificationsCenter: React.FC<NotificationsCenterProps> = ({
  unreadCount,
  onUnreadCountChange,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const isDark = theme.palette.mode === 'dark';
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';
  const textPrimary = isDark ? '#ffffff' : NAUTILUS.deepOcean;
  const textSecondary = isDark ? '#b0bec5' : '#546e7a';
  const accent = isDark ? NAUTILUS.sand : NAUTILUS.deepOcean;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    loadNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const loadNotifications = async (pageNum: number = 1, append: boolean = false) => {
    setLoading(true);
    try {
      console.log('Loading notifications for page', pageNum);
      const response = await notificationsService.getNotifications(pageNum, 10, false);
      console.log('Received notifications:', response.notifications);
      const newNotifications = append ? [...notifications, ...response.notifications] : response.notifications;
      setNotifications(newNotifications);
      setHasMore(pageNum < response.pagination.pages);
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await notificationsService.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, isRead: true } : notif
        )
      );
      onUnreadCountChange(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleDelete = async (notificationId: number) => {
    try {
      await notificationsService.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
      const deletedNotif = notifications.find(n => n.id === notificationId);
      if (deletedNotif && !deletedNotif.isRead) {
        onUnreadCountChange(Math.max(0, unreadCount - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleDeleteAll = async () => {
    if (notifications.length === 0) return;

    try {
      await notificationsService.deleteAllNotifications();
      setNotifications([]);
      onUnreadCountChange(0);
      setHasMore(false);
      setPage(1);
    } catch (error) {
      console.error('Failed to delete all notifications:', error);
    }
  };

  const handleLoadMore = () => {
    loadNotifications(page + 1, true);
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }

    handleClose();

    try {
      await navigateFromNotification(notification, navigate);
    } catch (error) {
      console.error('Failed to navigate from notification:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label="Notifications"
        aria-haspopup="true"
        aria-expanded={open}
        sx={{
          color: theme => theme.palette.mode === 'dark' ? '#0f2134' : 'inherit'
        }}
      >
        <Badge
          badgeContent={unreadCount}
          sx={{
            '& .MuiBadge-badge': {
              bgcolor: NAUTILUS.sand,
              color: NAUTILUS.deepOcean,
              fontWeight: 'bold'
            }
          }}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        elevation={0}
        PaperProps={{
          sx: {
            mt: 1,
            width: 460,
            maxWidth: 'calc(100vw - 24px)',
            maxHeight: 'min(520px, calc(100vh - 80px))',
            overflowY: 'auto',
            borderRadius: 1,
            bgcolor: surfaceBg,
            border: `1px solid ${panelBorder}`,
            boxShadow: 'none',
            color: textPrimary,
            scrollbarWidth: 'thin',
            scrollbarColor: `${panelBorder} transparent`,
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: panelBorder,
              borderRadius: 8,
            },
            '& ::selection': {
              backgroundColor: alpha(NAUTILUS.sand, 0.35),
              color: textPrimary,
            },
          },
        }}
        sx={getZoomCompensatedSx()}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            px: 2.5,
            py: 1.75,
            borderBottom: `1px solid ${panelBorder}`,
            bgcolor: surfaceBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, minWidth: 0 }}>
            <Typography
              component="h2"
              sx={{
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontSize: '0.75rem',
                lineHeight: 1.4,
                color: textSecondary,
              }}
            >
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Typography
                sx={{
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '0.7rem',
                  color: accent,
                }}
              >
                {unreadCount} unread
              </Typography>
            )}
          </Box>
          <Button
            size="small"
            onClick={handleDeleteAll}
            disabled={notifications.length === 0 || loading}
            sx={{
              flexShrink: 0,
              minWidth: 'auto',
              px: 1.25,
              py: 0.5,
              borderRadius: 1,
              border: `1px solid ${panelBorder}`,
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: accent,
              '&:hover': {
                borderColor: isDark ? alpha(NAUTILUS.sand, 0.45) : alpha(NAUTILUS.deepOcean, 0.35),
                bgcolor: isDark ? alpha(NAUTILUS.sand, 0.08) : alpha(NAUTILUS.deepOcean, 0.06),
              },
              '&.Mui-disabled': {
                color: textSecondary,
                borderColor: panelBorder,
              },
            }}
          >
            Delete all
          </Button>
        </Box>

        {notifications.length === 0 && !loading && (
          <Box sx={{ px: 3, py: 6, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 28, color: textSecondary, mb: 1.25 }} />
            <Typography sx={{ fontSize: '0.9375rem', color: textSecondary }}>
              No notifications
            </Typography>
          </Box>
        )}

        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {notifications.map((notification) => {
            const unread = !notification.isRead;
            return (
              <Box
                component="li"
                key={notification.id}
                tabIndex={0}
                onClick={(event) => {
                  if ((event.target as HTMLElement).closest('button')) return;
                  handleNotificationClick(notification);
                }}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleNotificationClick(notification);
                  }
                }}
                sx={{
                  borderBottom: `1px solid ${panelBorder}`,
                  px: 2.5,
                  py: 1.75,
                  cursor: 'pointer',
                  bgcolor: unread
                    ? (isDark ? alpha(NAUTILUS.sand, 0.06) : alpha(NAUTILUS.deepOcean, 0.035))
                    : 'transparent',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    bgcolor: isDark ? alpha(NAUTILUS.sand, 0.1) : alpha(NAUTILUS.deepOcean, 0.05),
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${accent}`,
                    outlineOffset: -2,
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '8px minmax(0, 1fr) auto',
                    columnGap: 1.5,
                    rowGap: 0.75,
                    alignItems: 'center',
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      gridColumn: 1,
                      gridRow: 1,
                      width: 8,
                      height: 8,
                      mt: '6px',
                      justifySelf: 'center',
                      alignSelf: 'start',
                      borderRadius: '50%',
                      bgcolor: unread ? accent : 'transparent',
                    }}
                  />
                  <Typography
                    sx={{
                      gridColumn: 2,
                      gridRow: 1,
                      fontSize: '0.9375rem',
                      fontWeight: unread ? 600 : 400,
                      lineHeight: 1.45,
                      color: unread ? textPrimary : textSecondary,
                      overflowWrap: 'break-word',
                    }}
                  >
                    {notification.message}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Delete notification"
                    onClick={() => handleDelete(notification.id)}
                    sx={{
                      gridColumn: 3,
                      gridRow: 1,
                      justifySelf: 'end',
                      alignSelf: 'start',
                      mt: '-2px',
                      color: textSecondary,
                      '&:hover': {
                        color: textPrimary,
                        bgcolor: isDark ? alpha(NAUTILUS.sand, 0.08) : alpha(NAUTILUS.deepOcean, 0.06),
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Box
                    sx={{
                      gridColumn: 2,
                      gridRow: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        color: textSecondary,
                        lineHeight: 1.4,
                      }}
                    >
                      {notificationTypeLabel(notification.type)}
                      <Box component="span" sx={{ px: 0.75 }}>
                        ·
                      </Box>
                      {formatDate(notification.createdAt)}
                    </Typography>
                    {unread && (
                      <Button
                        size="small"
                        onClick={() => handleMarkAsRead(notification.id)}
                        sx={{
                          flexShrink: 0,
                          minWidth: 'auto',
                          px: 0.75,
                          py: 0.25,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                          color: accent,
                        }}
                      >
                        Mark read
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2.5 }}>
            <LoadingAnimation />
          </Box>
        )}

        {hasMore && !loading && notifications.length > 0 && (
          <Box sx={{ px: 2, py: 1.25, textAlign: 'center' }}>
            <Button
              onClick={handleLoadMore}
              size="small"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: accent,
              }}
            >
              Load more
            </Button>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default NotificationsCenter;
